import React from "react";
import { Table, Tr, Td, Tbody, Thead, Th, TableContainer, Flex, Box } from "@chakra-ui/react";
import { gradients } from "@looksrare/chakra-theme";
import { formatNumberToLocale, formatToSignificant, useGetCurrencyConfig } from "@looksrare/utils";
import { CheckmarkFilledIcon, CloudinaryImage, NextLink } from "@looksrare/uikit";
import { useTranslation } from "next-i18next";
import { SectionLoader, Text, Button } from "uikit";

import { TableFooter } from "components/TableFooter";
import { transformRafflesHistoryToProps } from "views/win/userGeneratedRaffles/components/HistoryTable/utils";
import { RafflesHistory, RaffleStatus } from "views/win/userGeneratedRaffles/types";
import { RAFFLE_HISTORY_PER_PAGE } from "views/win/userGeneratedRaffles/utils/hooks";
import { ClaimRefundButton } from "views/win/userGeneratedRaffles/components/ClaimRefundButton";

interface UGRHistoryTableProps {
  page: number;
  setPage: (page: number) => void;
  totalRows: number;
  raffles: RafflesHistory[];
  isLoading?: boolean;
}

export const HistoryTable = ({ page, setPage, totalRows, raffles, isLoading = false }: UGRHistoryTableProps) => {
  const { t } = useTranslation();
  const getCurrencyConfig = useGetCurrencyConfig();

  return (
    <Box>
      <TableContainer
        border="solid 1px"
        borderColor="border-01"
        bgGradient={gradients["ui-gradient-v-01"]}
        borderRadius="dialog"
      >
        <Table size="lg">
          <Thead>
            <Tr>
              <Th>{t("Raffle #")}</Th>
              <Th textAlign="left">{t("Raffle Name")}</Th>
              <Th textAlign="right">{t("Value")}</Th>
              <Th textAlign="right">
                <Box>{t("Total Tickets")}</Box>
                <Box>{t("Bought (Global)")}</Box>
              </Th>
              <Th textAlign="right">
                <Box>{t("Total Tickets")}</Box>
                <Box>{t("(Target)")}</Box>
              </Th>
              <Th textAlign="right">{t("Completion %")}</Th>
              <Th textAlign="right">{t("Participants")}</Th>
              <Th textAlign="right">{t("Status")}</Th>
              <Th textAlign="right">{t("Date")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {raffles &&
              raffles.map((raffle) => {
                const {
                  raffleNumber,
                  raffleName,
                  totalEntriesBought,
                  totalEntriesTarget,
                  numberParticipants,
                  status,
                  date,
                  isUserParticipant,
                  refundClaimed,
                  value,
                } = transformRafflesHistoryToProps(raffle);

                const statusElement = (() => {
                  const titleCaseStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                  if (isUserParticipant) {
                    if (refundClaimed) {
                      return (
                        <Button size="xs" isDisabled rightIcon={<CheckmarkFilledIcon />}>
                          {t("Claimed")}
                        </Button>
                      );
                    }

                    if (status === RaffleStatus.REFUNDABLE) {
                      return <ClaimRefundButton raffleOnChainId={BigInt(raffle.raffleOnChainId.toString())} />;
                    }
                  }

                  return (
                    <Text textStyle="detail" bold noOfLines={1}>
                      {[RaffleStatus.CANCELLED, RaffleStatus.REFUNDABLE].includes(status)
                        ? t("Canceled")
                        : titleCaseStatus}
                    </Text>
                  );
                })();

                const completionPercentage = (() => {
                  if (totalEntriesTarget === 0 || totalEntriesBought === 0) {
                    return "0%";
                  }
                  return `${formatNumberToLocale((100 * totalEntriesBought) / totalEntriesTarget, 0)}%`;
                })();

                const prizeNft = raffle.prizes.find((prize) => !!prize.token);
                const { icon: CurrencyIcon, symbol } = getCurrencyConfig(raffle.feeCurrency);
                const isCurrencyEth = symbol === "ETH";

                return (
                  <Tr
                    key={`${raffle.version}-${raffle.raffleOnChainId.toString()}`}
                    fontSize="14px"
                    borderBottom="1px solid"
                    borderBottomColor="border-01"
                    borderLeft={isUserParticipant ? "solid 4px" : "transparent 4px"}
                    borderLeftColor={isUserParticipant ? "interactive-03" : "ui-01"}
                  >
                    {/* Raffle Number */}
                    <Td>
                      <Text textStyle="detail" color="text-03" noOfLines={1} textAlign="center">
                        {raffleNumber}
                      </Text>
                    </Td>

                    {/* Avatar & Name */}
                    <Td>
                      <Flex alignItems="center" gap={4}>
                        <Box w={10} h={10} flexShrink="0" position="relative" borderRadius="button" overflow="hidden">
                          <CloudinaryImage
                            src={prizeNft!.token!.image.src}
                            contentType={prizeNft!.token!.image.contentType}
                            objectFit="cover"
                            alt={prizeNft!.token!.name}
                            width={40}
                            height={40}
                          />
                        </Box>
                        <Text
                          as={NextLink}
                          href={`/raffles/${raffle.version}/${raffle.raffleOnChainId.toString()}`}
                          textStyle="detail"
                          bold
                          noOfLines={1}
                        >
                          {raffleName}
                        </Text>
                      </Flex>
                    </Td>

                    {/* Value */}
                    <Td textAlign="right">
                      <Flex alignItems="center" gap={2}>
                        <CurrencyIcon height="16px" width={isCurrencyEth ? "8px" : "16px"} />
                        <Text textStyle="detail" bold>
                          {formatToSignificant(value, 4)}
                        </Text>
                      </Flex>
                    </Td>
                    {/* Total Entries */}
                    <Td textAlign="right">
                      <Text textStyle="detail" bold>
                        {formatNumberToLocale(totalEntriesBought, 0)}
                      </Text>
                    </Td>

                    {/* Total Entries Target */}
                    <Td textAlign="right">
                      <Text textStyle="detail" bold>
                        {formatNumberToLocale(totalEntriesTarget, 0)}
                      </Text>
                    </Td>

                    {/* Completion % */}
                    <Td textAlign="right">
                      <Text textStyle="detail" bold>
                        {completionPercentage}
                      </Text>
                    </Td>

                    {/* Participants */}
                    <Td textAlign="right">
                      <Text textStyle="detail" bold noOfLines={1}>
                        {formatNumberToLocale(numberParticipants, 0)}
                      </Text>
                    </Td>

                    {/* Status */}
                    <Td textAlign="right">{statusElement}</Td>

                    {/* Date */}
                    <Td textAlign="right" color="text-03">
                      <Text textStyle="detail" color="text-03">
                        {date}
                      </Text>
                    </Td>
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
        {isLoading && (
          <Flex flexDirection="column" alignItems="center" p={8}>
            <SectionLoader />
          </Flex>
        )}
        <TableFooter
          currentPageNumber={page}
          goToPage={setPage}
          totalPagesNumber={totalRows ? Math.ceil(totalRows / RAFFLE_HISTORY_PER_PAGE) : 1}
          labelPrefix="raffle-history"
        />
      </TableContainer>
    </Box>
  );
};
